import React from 'react';

const ResultSummary = ({ data }) => {
    const cagrTheme = { bg: 'bg-primary', text: 'text-white' };
    const xirrTheme = { bg: 'bg-warning', text: 'text-dark' };
    const formatInr = (value) => {
        if (value === null || value === undefined || value === '') {
            return value;
        }

        const numberValue = Number(value);
        if (Number.isNaN(numberValue)) {
            return value;
        }

        const formatted = new Intl.NumberFormat('en-IN', {
            maximumFractionDigits: 2,
        }).format(numberValue);

        const absValue = Math.abs(numberValue);
        let approx = '';
        if (absValue >= 10000000) {
            const croreValue = absValue / 10000000;
            const croreDigits = absValue >= 100000000 ? 0 : 1;
            const sign = numberValue < 0 ? '-' : '';
            approx = ` (~${sign}${croreValue.toFixed(croreDigits)}C)`;
        } else if (absValue >= 100000) {
            const lakhValue = absValue / 100000;
            const lakhDigits = absValue >= 1000000 ? 0 : 1;
            const sign = numberValue < 0 ? '-' : '';
            approx = ` (~${sign}${lakhValue.toFixed(lakhDigits)}L)`;
        }

        return `${formatted}${approx}`;
    };

    const {
        cagr,
        netCagr,
        xirr,
        netXirr,
        totalTrades,
        successPercentage,
        avgProfitPercentage,
        minProfitPercentage,
        maxProfitPercentage,
        totalBuyOverall,
        totalSellOverall,
        minimumInitialCashRequired,
        currentCashInHand,
        currentStockInvested,
        totalCurrentValueAtEnd,
        totalCapitalGainsTax,
        totalInvested,
        totalReturns,
        totalGrossProfit,
        totalBrokerage,
        totalOtherChargesAndTax,
        totalCharges,
        totalNetProfit,
        totalInvestedOpen,
        totalCurrentValueOpen,
        grossProfitOpen,
        totalBrokerageOpen,
        totalOtherChargesAndTaxOpen,
        totalChargesOpen,
        netProfitOpen,
    } = data;

    return (
        <div className="row">
            <div className="col-md-3">
                <div className="card text-white bg-primary mb-3">
                    <div className="card-body">
                        <h5 className="card-title">Total Trades</h5>
                        <p className="card-text">{totalTrades}</p>
                    </div>
                </div>
            </div>
            <div className="col-md-3">
                <div className="card text-white bg-success mb-3">
                    <div className="card-body">
                        <h5 className="card-title">Success Rate</h5>
                        <p className="card-text">{successPercentage}%</p>
                    </div>
                </div>
            </div>
            <div className="col-md-3">
                <div className="card text-dark bg-warning mb-3">
                    <div className="card-body">
                        <h5 className="card-title">Avg Net Profit%</h5>
                        <p className="card-text">{avgProfitPercentage}%</p>
                    </div>
                </div>
            </div>
            <div className="col-md-3">
                <div className="card text-white bg-secondary mb-3">
                    <div className="card-body">
                        <h5 className="card-title">Min/Max Net Profit%</h5>
                        <p className="card-text">{minProfitPercentage}% / {maxProfitPercentage}%</p>
                    </div>
                </div>
            </div>
            <div className="col-md-6">
                <div className="card text-white bg-secondary mb-3">
                    <div className="card-body">
                        <h5 className="card-title">Completed Trades Ledger</h5>
                        <p className="card-text">Total Invested: {formatInr(totalInvested)}</p>
                        <p className="card-text">Total Returns: {formatInr(totalReturns)}</p>
                        <p className="card-text"><strong>Gross Profit (without charges): {formatInr(totalGrossProfit)}</strong></p>
                        <p className="card-text">Brokerage: {formatInr(totalBrokerage)}</p>
                        <p className="card-text">Other Charges & Tax: {formatInr(totalOtherChargesAndTax)}</p>
                        <p className="card-text">Total Charges: {formatInr(totalCharges)}</p>
                        <p className="card-text">Total Capital Gains Tax: {formatInr(totalCapitalGainsTax)}</p>
                        <p className="card-text"><strong>Net Profit (after charges): {formatInr(totalNetProfit)}</strong></p>
                    </div>
                </div>
            </div>
            <div className="col-md-6">
                <div className="card text-white bg-secondary mb-3">
                    <div className="card-body">
                        <h5 className="card-title">Open Trades Ledger</h5>
                        <p className="card-text">Total Invested: {formatInr(totalInvestedOpen)}</p>
                        <p className="card-text">Current Value: {formatInr(totalCurrentValueOpen)}</p>
                        <p className="card-text"><strong>Gross Profit (without charges): {formatInr(grossProfitOpen)}</strong></p>
                        <p className="card-text">Unrealized Brokerage: {formatInr(totalBrokerageOpen)}</p>
                        <p className="card-text">Unrealized Other Charges & Tax: {formatInr(totalOtherChargesAndTaxOpen)}</p>
                        <p className="card-text">Unrealized Total Charges: {formatInr(totalChargesOpen)}</p>
                        <p className="card-text"><strong>Unrealized Net Profit (after charges): {formatInr(netProfitOpen)}</strong></p>
                    </div>
                </div>
            </div>
            <div className="col-md-3">
                <div className={`card ${cagrTheme.text} ${cagrTheme.bg} mb-3`}>
                    <div className="card-body">
                        <h5 className="card-title">Gross CAGR</h5>
                        <p className="card-text">{cagr}%</p>
                    </div>
                </div>
            </div>
            <div className="col-md-3">
                <div className={`card ${cagrTheme.text} ${cagrTheme.bg} mb-3`}>
                    <div className="card-body">
                        <h5 className="card-title">Net CAGR</h5>
                        <p className="card-text">{netCagr}%</p>
                    </div>
                </div>
            </div>
            <div className="col-md-3">
                <div className={`card ${xirrTheme.text} ${xirrTheme.bg} mb-3`}>
                    <div className="card-body">
                        <h5 className="card-title">Gross XIRR</h5>
                        <p className="card-text">{xirr}%</p>
                    </div>
                </div>
            </div>
            <div className="col-md-3">
                <div className={`card ${xirrTheme.text} ${xirrTheme.bg} mb-3`}>
                    <div className="card-body">
                        <h5 className="card-title">Net XIRR</h5>
                        <p className="card-text">{netXirr}%</p>
                    </div>
                </div>
            </div>
            <div className="col-md-6">
                <div className="card text-white bg-dark mb-3">
                    <div className="card-body">
                        <h5 className="card-title">Capital Breakdown</h5>
                        <p className="card-text">1. Minimum initial cash required: {formatInr(minimumInitialCashRequired)}</p>
                        <p className="card-text">2. Final cash in hand (from trade flow): {formatInr(currentCashInHand)}</p>
                        <p className="card-text">3. Current invested as stocks (cost): {formatInr(currentStockInvested)}</p>
                        <p className="card-text">4. Current stock value (market): {formatInr(totalCurrentValueOpen)}</p>
                        <p className="card-text">5. Total buys (overall): {totalBuyOverall}</p>
                        <p className="card-text">6. Total sells (overall): {totalSellOverall}</p>
                        <p className="card-text"><strong>7. Total current value (cash + stock): {formatInr(totalCurrentValueAtEnd)}</strong></p>
                    </div>
                </div>
            </div>


        </div>
    );
};

export default ResultSummary;
